import {
  DataModel, SerializationContext, DataModelBuilder, SerializedData, isSameData,
} from '../dto/dto.js';
import { MapUtils } from '../../utility/map-utils.js';
import { Price } from './price.js';
import { PriceRange } from './price-range.js';
import { Fee } from './fee.js';
import { VersionRange } from '../core/version-range.js';

export class FeeRange extends DataModel {
  static readonly TYPE_ID: string = 'bca5bc4bd7364ed388da1c8bbd70f840';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly priceRange: PriceRange,
    public readonly fee: Fee,
  ) {
    super();
  }

  get $typeId(): string {
    return FeeRange.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(FeeRange.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): FeeRange {
    return new FeeRange(
        context.fromData<PriceRange>(MapUtils.get(map, 'priceRange')),
        context.fromData<Fee>(MapUtils.get(map, 'fee')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'priceRange': context.toData(this.priceRange),
      'fee': context.toData(this.fee),
    };
  }

  clone(): this {
    return new FeeRange(
        this.priceRange.clone(),
        this.fee.clone(),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.priceRange, this.priceRange) && isSameData(other.fee, this.fee);
  }

  isInRange(price: Price): boolean {
    if(price.currency !== this.priceRange.from.currency || price.currency !== this.priceRange.to.currency) {
      return false;
    }

    return price.compareTo(this.priceRange.from) >= 0 && price.compareTo(this.priceRange.to) <= 0;
  }
}

export class FeeRangeBuilder extends DataModelBuilder<FeeRange> {
  build(context: SerializationContext, map: SerializedData): FeeRange {
    return FeeRange.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = VersionRange.mustDecode(map['$schema_version']);

    if(versionRange.contains(FeeRange.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(FeeRange.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version `fee` of type `Price` was changed to type `Fee`.
    if(version < 2) {
      const fee = Price.decode(MapUtils.get<string>(map, 'fee'));
      map['fee'] = context.toData(Fee.fixed(fee));
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
